import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { config } from "../../../../../Helpers/env";

/* ═══════════════════════════════════════════════════════════════
   useGBVAssessment — Custom hook for Gender-Based Violence
   Screening & Management Module (Task 20).
   Manages confidential GBV assessment, clinical management,
   referral tracking, consent/safety planning, and case history.
   Access restricted to authorized GBV focal persons.
   ═══════════════════════════════════════════════════════════════ */

 // Set to false to use real backend API calls. Keep true only for local UI-only mock debugging.
const USE_MOCK_DATA = false;

/* ─── Constants ─── */

export const GBV_TYPES = [
    { value: "physical", label: "Physical Violence", description: "Hitting, kicking, slapping, burning, use of weapons" },
    { value: "sexual", label: "Sexual Violence", description: "Rape, attempted rape, defilement, sexual harassment" },
    { value: "emotional", label: "Emotional / Psychological", description: "Threats, intimidation, humiliation, controlling behaviour" },
    { value: "economic", label: "Economic Violence", description: "Denial of resources, property grabbing, financial control" },
    { value: "neglect", label: "Neglect", description: "Denial of basic needs, abandonment, withholding care" },
];

export const INJURY_LOCATIONS = [
    { value: "head_face", label: "Head / Face" },
    { value: "neck", label: "Neck" },
    { value: "chest", label: "Chest" },
    { value: "abdomen", label: "Abdomen" },
    { value: "back", label: "Back" },
    { value: "upper_extremities", label: "Upper Extremities (Arms/Hands)" },
    { value: "lower_extremities", label: "Lower Extremities (Legs/Feet)" },
    { value: "genitalia", label: "Genitalia" },
    { value: "multiple", label: "Multiple Sites" },
    { value: "no_visible", label: "No Visible Injuries" },
];

export const INJURY_TYPES = [
    { value: "bruises", label: "Bruises / Contusions" },
    { value: "lacerations", label: "Lacerations / Cuts" },
    { value: "fractures", label: "Fractures" },
    { value: "burns", label: "Burns" },
    { value: "bite_marks", label: "Bite Marks" },
    { value: "strangulation_marks", label: "Strangulation Marks" },
    { value: "genital_injuries", label: "Genital / Anal Injuries" },
    { value: "swelling", label: "Swelling / Oedema" },
    { value: "other", label: "Other" },
];

export const CLINICAL_MANAGEMENT_ITEMS = [
    { key: "wound_care", label: "Wound Care / Injury Treatment" },
    { key: "sti_prophylaxis", label: "STI Prophylaxis" },
    { key: "ec", label: "Emergency Contraception (within 120hrs)" },
    { key: "hiv_pep", label: "HIV PEP (within 72hrs)" },
    { key: "hep_b_vaccine", label: "Hepatitis B Vaccination" },
    { key: "tetanus", label: "Tetanus Prophylaxis" },
];

export const REFERRAL_TYPES = [
    { key: "psychosocial_referral", label: "Psychosocial Support / Counselling", field: "psychosocial_referral" },
    { key: "police_referral", label: "Police / Law Enforcement", field: "police_referral" },
    { key: "safe_shelter_referral", label: "Safe Shelter / Safe House", field: "safe_shelter_referral" },
    { key: "legal_aid_referral", label: "Legal Aid / FIDA", field: "legal_aid_referral" },
];

/* ─── Mock Data ─── */

const createMockAssessments = function (patient_number) {
    return [
        {
            id: 1,
            patient_number: patient_number,
            visit_id: "V-2001",
            screening_date: "2025-11-10",
            gbv_type: "physical",
            disclosure_details: "Patient reports being hit by intimate partner. Incident occurred at home. First reported occurrence.",
            injuries_documented: [
                { location: "head_face", type: "bruises", description: "Bruise on left cheek, approximately 3cm diameter" },
                { location: "upper_extremities", type: "bruises", description: "Grip marks on both upper arms" },
            ],
            clinical_management: "Wound care, tetanus",
            psychosocial_support_given: true,
            psychosocial_referral: "Referred to MIFUMI Project counsellor — appointment 12 Nov 2025",
            referral_type: "psychosocial",
            pf3_completed: false,
            consent_obtained: true,
            safety_plan_discussed: true,
            follow_up_date: "2025-11-17",
            assessed_by: "Dr. Grace Nalubega",
            access_level: "restricted",
            created_at: "2025-11-10T09:45:00Z",
        },
        {
            id: 2,
            patient_number: patient_number,
            visit_id: "V-1850",
            screening_date: "2025-06-22",
            gbv_type: "sexual",
            disclosure_details: "Patient disclosed sexual assault by unknown assailant. Occurred 18 hours prior to presentation.",
            injuries_documented: [
                { location: "genital_injuries", type: "lacerations", description: "Perineal tear noted on examination" },
                { location: "upper_extremities", type: "bruises", description: "Defensive injuries on forearms" },
            ],
            clinical_management: "Wound care, STI prophylaxis, EC, HIV PEP, Hep B vaccine, tetanus",
            psychosocial_support_given: true,
            psychosocial_referral: "Crisis counselling provided. Referred to Raising Voices for ongoing support.",
            referral_type: "police",
            pf3_completed: true,
            consent_obtained: true,
            safety_plan_discussed: true,
            follow_up_date: "2025-06-25",
            assessed_by: "Dr. Sarah Nakamya",
            access_level: "restricted",
            created_at: "2025-06-22T14:30:00Z",
        },
    ];
};

/* ─── Initial Form State ─── */

const createInitialFormState = function () {
    return {
        screening_date: new Date().toISOString().split("T")[0],
        gbv_type: "",
        disclosure_details: "",
        injuries_documented: [],
        clinical_management: "",
        psychosocial_support_given: false,
        psychosocial_referral: "",
        referral_type: "",
        pf3_completed: false,
        consent_obtained: false,
        safety_plan_discussed: false,
        follow_up_date: "",
    };
};

/* ─── Injury Entry Helper ─── */

const createEmptyInjury = function () {
    return {
        location: "",
        type: "",
        description: "",
    };
};

/* ═══════════════════════════════════════════════════════════════
   Hook
   ═══════════════════════════════════════════════════════════════ */

export const useGBVAssessment = function (actions, patient_number, visit_id) {
    /* ─── State ─── */
    const loadingState = useState(true);
    const loading = loadingState[0];
    const setLoading = loadingState[1];

    const submittingState = useState(false);
    const submitting = submittingState[0];
    const setSubmitting = submittingState[1];

    const attemptedSubmitState = useState(false);
    const hasAttemptedSubmit = attemptedSubmitState[0];
    const setHasAttemptedSubmit = attemptedSubmitState[1];

    const assessmentsState = useState([]);
    const assessments = assessmentsState[0];
    const setAssessments = assessmentsState[1];

    const formState = useState(createInitialFormState());
    const form = formState[0];
    const setForm = formState[1];

    const tabState = useState("assessment");
    const activeTab = tabState[0];
    const setActiveTab = tabState[1];

    const editingState = useState(null);
    const editingRecord = editingState[0];
    const setEditingRecord = editingState[1];

    const consentDialogState = useState(false);
    const showConsentDialog = consentDialogState[0];
    const setShowConsentDialog = consentDialogState[1];

    /* ─── Snackbar helper ─── */
    const showSnackbar = useCallback(function (type, message) {
        if (!actions || !actions.snackbarActions) return;
        const snack = actions.snackbarActions;
        if (typeof snack.snackbarOpen === "function") {
            return snack.snackbarOpen(type, message);
        }
        if (type === "success" && typeof snack.snackSuccess === "function") {
            return snack.snackSuccess(message);
        }
        if (type === "error" && typeof snack.snackError === "function") {
            return snack.snackError(message);
        }
        if (type === "warning" && typeof snack.snackWarning === "function") {
            return snack.snackWarning(message);
        }
        if (type === "info" && typeof snack.snackInfo === "function") {
            return snack.snackInfo(message);
        }

        if (type === "success" && typeof snack.success === "function") {
            return snack.success(message);
        }
        if (type === "error" && typeof snack.error === "function") {
            return snack.error(message);
        }
    }, [actions]);

    /* ─── Fetch Assessments ─── */
    const fetchAssessments = useCallback(function () {
        setLoading(true);
        if (USE_MOCK_DATA) {
            setTimeout(function () {
                setAssessments(createMockAssessments(patient_number));
                setLoading(false);
            }, 500);
            return;
        }

        axios.get(config.smsUrl + "/obgyn/get_gbv_assessments", {
            params: { patient_number: patient_number },
            headers: { "Authorization": "Bearer " + sessionStorage.getItem("token") },
        }).then(function (res) {
            const data = res.data || [];
            setAssessments(Array.isArray(data) ? data : (data.assessments || []));
            setLoading(false);
        }).catch(function (err) {
            console.error("Failed to fetch GBV assessments:", err);
            setLoading(false);
            showSnackbar("error", "Failed to load GBV assessment history");
        });
    }, [patient_number, actions, showSnackbar]);

    useEffect(function () {
        if (patient_number) {
            fetchAssessments();
        }
    }, [patient_number, fetchAssessments]);

    /* ─── Handle Form Change ─── */
    const handleChange = useCallback(function (field, value) {
        setForm(function (prev) {
            const updated = Object.assign({}, prev);
            updated[field] = value;

            // Auto-set follow-up date to 7 days from screening if not set
            if (field === "screening_date" && !prev.follow_up_date) {
                const d = new Date(value);
                if (!isNaN(d.getTime())) {
                    d.setDate(d.getDate() + 7);
                    updated.follow_up_date = d.toISOString().split("T")[0];
                }
            }

            return updated;
        });
    }, []);

    /* ─── Handle Injury Management ─── */
    const addInjury = useCallback(function () {
        setForm(function (prev) {
            return Object.assign({}, prev, {
                injuries_documented: prev.injuries_documented.concat([createEmptyInjury()]),
            });
        });
    }, []);

    const updateInjury = useCallback(function (index, field, value) {
        setForm(function (prev) {
            const newInjuries = prev.injuries_documented.map(function (inj, i) {
                if (i === index) {
                    const updated = Object.assign({}, inj);
                    updated[field] = value;
                    return updated;
                }
                return inj;
            });
            return Object.assign({}, prev, { injuries_documented: newInjuries });
        });
    }, []);

    const removeInjury = useCallback(function (index) {
        setForm(function (prev) {
            return Object.assign({}, prev, {
                injuries_documented: prev.injuries_documented.filter(function (_, i) { return i !== index; }),
            });
        });
    }, []);

    const getValidationErrors = useCallback(function (targetForm) {
        const errors = {};
        if (!targetForm.screening_date) {
            errors.screening_date = "Assessment date is required";
        }
        if (!targetForm.gbv_type) {
            errors.gbv_type = "Type of violence is required";
        }
        if (!targetForm.consent_obtained) {
            errors.consent_obtained = "Patient consent must be obtained before documentation";
        }
        if (!targetForm.disclosure_details || targetForm.disclosure_details.trim().length < 10) {
            errors.disclosure_details = "Disclosure details are required (minimum 10 characters)";
        }
        return errors;
    }, []);

    /* ─── Validation ─── */
    const validationErrors = useMemo(function () {
        return getValidationErrors(form);
    }, [form, getValidationErrors]);

    const isFormValid = useMemo(function () {
        return Object.keys(validationErrors).length === 0;
    }, [validationErrors]);

    /* ─── Reset ─── */
    const resetForm = useCallback(function () {
        setHasAttemptedSubmit(false);
        setForm(createInitialFormState());
        setEditingRecord(null);
    }, []);

    const submitForm = useCallback(function (formData) {
        setSubmitting(true);

        const payload = Object.assign({}, formData, {
            patient_number: patient_number,
            visit_id: visit_id,
            access_level: "restricted",
        });

        if (USE_MOCK_DATA) {
            setTimeout(function () {
                if (editingRecord) {
                    setAssessments(function (prev) {
                        return prev.map(function (a) {
                            if (a.id === editingRecord.id) {
                                return Object.assign({}, a, payload, { created_at: a.created_at });
                            }
                            return a;
                        });
                    });
                    showSnackbar("success", "GBV assessment updated successfully");
                } else {
                    const newRecord = Object.assign({}, payload, {
                        id: Date.now(),
                        assessed_by: "Current User",
                        created_at: new Date().toISOString(),
                    });
                    setAssessments(function (prev) { return [newRecord].concat(prev); });
                    showSnackbar("success", "GBV assessment recorded confidentially");
                }
                resetForm();
                setSubmitting(false);
                setActiveTab("history");
            }, 600);
            return;
        }

        const url = editingRecord
            ? config.smsUrl + "/obgyn/update_gbv_assessment?assessment_id=" + editingRecord.id
            : config.smsUrl + "/obgyn/create_gbv_assessment";
        const method = editingRecord ? "put" : "post";

        axios[method](url, payload, {
            headers: { "Authorization": "Bearer " + sessionStorage.getItem("token") },
        }).then(function () {
            if (actions && actions.snackbarActions) {
                showSnackbar("success",
                    editingRecord ? "GBV assessment updated" : "GBV assessment recorded confidentially"
                );
            }
            resetForm();
            fetchAssessments();
            setSubmitting(false);
            setActiveTab("history");
        }).catch(function (err) {
            console.error("Failed to save GBV assessment:", err);
            setSubmitting(false);
            if (actions && actions.snackbarActions) {
                showSnackbar("error", "Failed to save GBV assessment");
            }
        });
    }, [editingRecord, patient_number, visit_id, actions, fetchAssessments, showSnackbar, resetForm]);

    const handleSubmit = useCallback(function () {
        setHasAttemptedSubmit(true);

        if (!form.consent_obtained) {
            setShowConsentDialog(true);
            return;
        }

        if (!isFormValid) {
            showSnackbar("error", "Please fill in all required fields");
            return;
        }

        submitForm(form);
    }, [form, isFormValid, submitForm, setShowConsentDialog, showSnackbar]);

    const handleSubmitConfirmed = useCallback(function () {
        const confirmedForm = Object.assign({}, form, { consent_obtained: true });
        setHasAttemptedSubmit(true);
        setShowConsentDialog(false);
        setForm(function (prev) {
            return Object.assign({}, prev, { consent_obtained: true });
        });

        const confirmedErrors = getValidationErrors(confirmedForm);
        if (Object.keys(confirmedErrors).length > 0) {
            showSnackbar("error", "Please fill in all required fields");
            return;
        }

        submitForm(confirmedForm);
    }, [form, getValidationErrors, setForm, setShowConsentDialog, showSnackbar, submitForm]);

    /* ─── Edit ─── */
    const startEdit = useCallback(function (record) {
        setHasAttemptedSubmit(false);
        setEditingRecord(record);
        setForm({
            screening_date: record.screening_date || "",
            gbv_type: record.gbv_type || "",
            disclosure_details: record.disclosure_details || "",
            injuries_documented: record.injuries_documented || [],
            clinical_management: typeof record.clinical_management === "string" ? record.clinical_management : "",
            psychosocial_support_given: record.psychosocial_support_given || false,
            psychosocial_referral: record.psychosocial_referral || "",
            referral_type: record.referral_type || "",
            pf3_completed: record.pf3_completed || false,
            consent_obtained: record.consent_obtained || false,
            safety_plan_discussed: record.safety_plan_discussed || false,
            follow_up_date: record.follow_up_date || "",
        });
        setActiveTab("assessment");
    }, []);

    /* ─── Cancel Edit ─── */
    const cancelEdit = useCallback(function () {
        resetForm();
    }, [resetForm]);

    /* ─── Summary Stats ─── */
    const summary = useMemo(function () {
        const totalAssessments = assessments.length;

        const lastAssessment = assessments.length > 0
            ? assessments.reduce(function (latest, a) {
                return new Date(a.screening_date) > new Date(latest.screening_date) ? a : latest;
            })
            : null;

        const hasCurrentVisitRecord = assessments.some(function (a) {
            return a.visit_id === visit_id;
        });

        const pendingFollowUps = assessments.filter(function (a) {
            if (!a.follow_up_date) return false;
            return new Date(a.follow_up_date) >= new Date();
        }).length;

        const referralsMade = assessments.reduce(function (count, a) {
            let c = 0;
            if (a.referral_type) c++;
            return count + c;
        }, 0);

        const typeBreakdown = {};
        assessments.forEach(function (a) {
            const type = a.gbv_type || "unknown";
            typeBreakdown[type] = (typeBreakdown[type] || 0) + 1;
        });

        return {
            totalAssessments: totalAssessments,
            lastAssessment: lastAssessment,
            hasCurrentVisitRecord: hasCurrentVisitRecord,
            pendingFollowUps: pendingFollowUps,
            referralsMade: referralsMade,
            typeBreakdown: typeBreakdown,
        };
    }, [assessments, visit_id]);

    /* ─── Alerts ─── */
    const alerts = useMemo(function () {
        const result = [];

        if (form.gbv_type === "sexual") {
            result.push({
                type: "critical",
                title: "Sexual Violence — Full Clinical Protocol Required",
                detail: "Ensure: STI prophylaxis, emergency contraception (within 120hrs), HIV PEP (within 72hrs), Hepatitis B vaccine, wound care, PF3 form, and psychosocial support.",
            });
        }

        if (form.gbv_type && !form.consent_obtained) {
            result.push({
                type: "warning",
                title: "Consent Required",
                detail: "Informed consent must be obtained from the patient before documenting GBV details and making referrals.",
            });
        }

        return result;
    }, [summary, form.gbv_type, form.consent_obtained]);

    /* ─── Return ─── */
    return {
        loading: loading,
        submitting: submitting,
        assessments: assessments,
        form: form,
        activeTab: activeTab,
        setActiveTab: setActiveTab,
        editingRecord: editingRecord,
        showConsentDialog: showConsentDialog,
        setShowConsentDialog: setShowConsentDialog,
        handleChange: handleChange,
        addInjury: addInjury,
        updateInjury: updateInjury,
        removeInjury: removeInjury,
        validationErrors: validationErrors,
        isFormValid: isFormValid,
        hasAttemptedSubmit: hasAttemptedSubmit,
        handleSubmit: handleSubmit,
        handleSubmitConfirmed: handleSubmitConfirmed,
        startEdit: startEdit,
        resetForm: resetForm,
        cancelEdit: cancelEdit,
        summary: summary,
        alerts: alerts,
    };
};
